import { Em, H1, H2, Li, P, Strong, Ul } from '../Prose';
export default function SectionSecurity() {
  return (
    <>
      <H1>Security</H1>

      <P>
        Whichever role you're working at M10c, you'll have access to not just
        our own data, but varoius types of data belonging to clients:
        intellectual property, customer data, and so on. Either directly because
        you've downloaded it to work with it, or indirectly via the credentials
        and systems you have access to.
      </P>

      <P>
        For this reason, it's extremely important that you take information
        security seriously, so that we can reassure our clients that we are
        following industry best practices when handling their data.
      </P>

      <P>The checklists below outline the steps you should be taking.</P>

      <H2>The office</H2>

      <Ul>
        <Li>
          If you are the last one to leave the office, you should lock the door
          behind you, <Em>with no exceptions</Em> - even if just nipping to the
          use the restroom.
        </Li>
        <Li>
          For the reason above, you should always keep your key on you when you
          leave the office, so you don't end up getting locked out.
        </Li>
        <Li>
          If someone you don't recognise tries to enter the office, make sure
          someone at M10c has confirmed they have a legitimate reason to be
          there - don't fall for unverifiable excuses e.g. "I'm here to pick up
          a laptop for servicing".
        </Li>
      </Ul>

      <H2>Your computer</H2>

      <Ul>
        <Li>
          Keep a clean physical separation between your work laptop and your
          personal one. I.e. work files and accounts should never be used on
          your personal computer, and likewise you should avoid using personal
          ones on your work computer.
        </Li>
        <Li>
          Make sure your laptop is setup with full-disk encryption enabled (i.e.
          if someone were to steal your laptop, the data on the hard drive is
          useless without the password).
        </Li>
        <Li>
          Never use removable storage media (e.g. USB drive) - sharing files
          between machines can be done with Dropbox, Github, etc
        </Li>
        <Li>
          Make sure access to the computer is locked with a strong password --
          or even better, using biometric authentication (e.g. fingerprint) to
          unlock.
        </Li>
        <Li>
          If using a password to unlock, ensure you're not being watched while
          you type in your password. If you think someone knows it, change it.
        </Li>
        <Li>
          Always lock your computer when walking away from it, never leave it
          unlocked and unattended.
        </Li>
        <Li>
          Make sure any important data is mirrored/stored in the correct place
          (e.g. code on GitHub, important files on Dropbox). Don't have anything
          sitting on your laptop's harddrive which you wouldn't be ok with
          losing.
        </Li>
        <Li>
          When you are no longer working on something, delete it from your
          computer. Keep it just to the projects and data which are currently
          relevant to you.
        </Li>
      </Ul>

      <H2>Your personal phone</H2>

      <Ul>
        <Li>
          Due to the more locked-down nature of mobile apps, it's ok to use your
          personal phone as a test device for mobile apps. But remember to
          delete the app when you're done working on it, and don't save anything
          into your downloads/files.
        </Li>
        <Li>
          To help with comms, you can download the Slack app and log into the
          company Slack on your personal phone, but don't download any files
          from it to your phone.
        </Li>
        <Li>
          You can, and should, use a TOTP app, such as Google Authenticator, on
          your personal phone for managing 2FA login codes to work accounts (as
          a separate device is needed from your work laptop to maintain
          security).
        </Li>
      </Ul>

      <H2>Service accounts</H2>

      <Ul>
        <Li>
          All accounts you use should be originating from your work email
          address (don't re-use personal accounts for work purposes).
        </Li>
        <Li>
          If you feel there is a situation where you need to re-use a personal
          account on your work computer, request approval first, or advice on an
          alternative.
        </Li>
        <Li>
          Use a strong, unique password for each account you sign up to, and
          store the password in KeePassXC password manager (with the database
          file on your work Dropbox user folder). This means there are only 2
          passwords you may need to write down externally: the password to
          unlock your work laptop, and your work KeePassXC database password.
        </Li>
        <Li>
          For the following critical services, as well as a password, set up
          TOTP Two-Factor Authentication, with an app like Google Authenticator:
          <Ul>
            <Li>Fastmail</Li>
            <Li>Dropbox</Li>
            <Li>GitHub</Li>
          </Ul>
        </Li>
        <Li>
          Make sure other users have access to services too and it isn't all
          hinged on your user account. Most services should have the concept of
          an "organisation", which should be set up for M10c as a whole and
          others added to as well as your user account.
        </Li>
        <Li>
          Most AI products will have it hidden in their terms that they can use
          data you provide to train their models, so (as with any other
          service), you should only upload client IP (such as snippets of code),
          if you are sure this isn't the case. The current approved AI services
          are:
          <Ul>
            <Li>
              ChatGPT Teams (<Strong>not</Strong> a personal ChatGPT account).
            </Li>
          </Ul>
        </Li>
      </Ul>
    </>
  );
}
